import { Flex, Grid, Typography } from 'antd'
import type { CSSProperties, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { BrandWordmark, BRAND_LOGO_DARK_SRC, BRAND_NAME } from 'src/components'
import { paths } from 'src/router'

import { useStyles } from './styles'

const { Link: AntdLink } = Typography
const { useBreakpoint } = Grid

type Props = {
  /** Left pane — the form, the wizard step, the summary. */
  children: ReactNode
  /** Right pane's contents. Omit for a single-column screen. */
  aside?: ReactNode
  /** Reading width of the left column. Unbounded when omitted. */
  contentMaxWidth?: number
  /** Language picker, for the surfaces that offer one. */
  footerControls?: ReactNode
  /**
   * Whether the wordmark links home. Off inside workspace creation: the flow is
   * a commitment in progress, and a logo that quietly abandons it is a trap.
   */
  brandLinked?: boolean
}

/**
 * The two-pane public stage: wordmark top-left, content centred, legal links
 * bottom-left, and one rounded inset slab down the right.
 *
 * Shared by the auth screens and by workspace creation. Both wore it before,
 * each with its own copy of the grid, so the paddings drifted apart and the
 * flow stopped reading as a continuation of signing in. The geometry lives here
 * once; what goes in the right pane is the caller's.
 */
export const PublicSplitShell = ({
  children,
  aside,
  contentMaxWidth,
  footerControls,
  brandLinked = true
}: Props) => {
  const { t } = useTranslation()
  const { styles, cx } = useStyles()
  const screens = useBreakpoint()

  const showAside = Boolean(aside) && screens.lg

  const innerStyle: CSSProperties | undefined =
    contentMaxWidth === undefined ? undefined : { maxWidth: contentMaxWidth }

  return (
    <div className={cx(styles.split, !showAside && styles.splitSolo)}>
      <div className={styles.contentPane}>
        <div className={styles.brandRow}>
          {brandLinked ? (
            <Link
              to={paths.home}
              aria-label={`${BRAND_NAME} home`}
              className={styles.brandLogoLink}
            >
              <BrandWordmark variant="auth" />
            </Link>
          ) : (
            <span className={styles.brandLogoLink}>
              <BrandWordmark variant="auth" />
            </span>
          )}
        </div>

        <div className={styles.contentCenter}>
          <div className={styles.contentInner} style={innerStyle}>
            {children}
          </div>
        </div>

        <Flex align="center" gap={16} wrap="wrap" className={styles.footerRow}>
          {footerControls ? (
            <Flex align="center" gap={8} className={styles.footerControls}>
              {footerControls}
            </Flex>
          ) : null}

          <Flex
            align="center"
            gap={16}
            wrap="wrap"
            className={styles.footerLinks}
          >
            <AntdLink href={paths.termsAndConditions} type="secondary">
              {t('terms_and_conditions')}
            </AntdLink>
            <AntdLink
              href={`mailto:support@skalx.ai?subject=${encodeURIComponent(`${BRAND_NAME} Help`)}`}
              type="secondary"
            >
              {t('contact_us')}
            </AntdLink>
          </Flex>
        </Flex>
      </div>

      {showAside ? (
        <div className={cx(styles.asidePane, styles.asideBrand)}>
          {/* Drawn here rather than passed in: every surface that wears this
              slab wants the same mark in the same corner, and handing it over as
              a prop meant two callers each carrying their own copy of the image
              and its styling. */}
          <img
            src={BRAND_LOGO_DARK_SRC}
            alt=""
            aria-hidden
            className={styles.asideWordmark}
          />
          {aside}
        </div>
      ) : null}
    </div>
  )
}
